/** `/catalog/products`, `/catalog/prizes`, `/catalog/prizes/:id` — lists, stock buckets, stock actions, substitution. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PrizeAdminDto, PrizeDetailDto, ProductAdminDto, StockAdjustRequest } from '@windrop/contracts';
import { Button, Field, Input, Select, useToast } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Can } from '../../auth/guards';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { isApiError } from '../../lib/api/client';
import { catalogApi } from './api';

const money = (m: { amountMinor: string; currency: string }) =>
  `${m.currency} ${(Number(m.amountMinor) / 100).toLocaleString()}`;

export function ProductsListPage() {
  const q = useQuery({ queryKey: ['products'], queryFn: () => catalogApi.products.list() });
  const columns = useMemo<Column<ProductAdminDto>[]>(
    () => [
      {
        id: 'name',
        header: 'Product',
        sortValue: (r) => r.name.en ?? '',
        cell: (r) => (
          <Link to={`/catalog/products/${r.productId}`}>
            <strong>{r.name.en}</strong>
          </Link>
        ),
      },
      { id: 'brand', header: 'Brand', sortValue: (r) => r.brandName ?? '', cell: (r) => r.brandName ?? '—' },
      { id: 'category', header: 'Category', cell: (r) => r.category },
      {
        id: 'skus',
        header: 'SKUs',
        align: 'end',
        sortValue: (r) => r.variantCount,
        cell: (r) => r.variantCount,
      },
      {
        id: 'issues',
        header: 'Asset issues',
        cell: (r) => (r.assetIssues.length ? `${r.assetIssues.length} to fix` : 'None'),
      },
    ],
    [],
  );
  return (
    <>
      <PageHeader
        title="Products"
        breadcrumb={[{ label: 'Sponsors & prizes', to: '/catalog' }, { label: 'Products' }]}
        actions={
          <Can permission="prizes.manage">
            <Button href="/catalog/products/new">New product</Button>
          </Can>
        }
      />
      <DataTable
        caption="Catalogue products"
        columns={columns}
        rows={q.data ?? []}
        rowKey={(r) => r.productId}
        loading={q.isPending}
        emptyState={
          <EmptyState
            title="No products yet"
            body="Build the first product: option axes, variant matrix, images per colour."
            action={<Button href="/catalog/products/new">New product</Button>}
          />
        }
      />
    </>
  );
}

export function PrizesListPage() {
  const q = useQuery({ queryKey: ['prizes', {}], queryFn: () => catalogApi.prizes.list() });
  const columns = useMemo<Column<PrizeAdminDto>[]>(
    () => [
      {
        id: 'name',
        header: 'Prize',
        sortValue: (r) => r.name.en ?? '',
        cell: (r) => (
          <Link to={`/catalog/prizes/${r.prizeId}`}>
            <strong>{r.name.en}</strong>
          </Link>
        ),
      },
      { id: 'tier', header: 'Tier', sortValue: (r) => r.tier, cell: (r) => r.tier },
      {
        id: 'mode',
        header: 'Mode',
        cell: (r) =>
          r.selectionMode === 'VALUE_CAP'
            ? `B · cap ${r.valueCap ? money(r.valueCap) : '—'}`
            : 'A · fixed product',
      },
      { id: 'value', header: 'Value', align: 'end', cell: (r) => money(r.value) },
      {
        id: 'available',
        header: 'Available',
        align: 'end',
        sortValue: (r) => r.stock.available,
        cell: (r) => r.stock.available,
      },
      {
        id: 'reserved',
        header: 'Reserved (main / sampling)',
        align: 'end',
        cell: (r) => `${r.stock.mainReserved} / ${r.stock.samplingReserved}`,
      },
      { id: 'won', header: 'Won', align: 'end', cell: (r) => r.stock.won },
      {
        id: 'lottery',
        header: 'Lottery',
        cell: (r) =>
          r.lotteryId ? (
            <Link to={`/lotteries/${r.lotteryId}`}>{r.lotteryId.slice(-6)}</Link>
          ) : (
            'Catalogue item'
          ),
        defaultHidden: true,
      },
    ],
    [],
  );
  return (
    <>
      <PageHeader
        title="Prizes"
        breadcrumb={[{ label: 'Sponsors & prizes', to: '/catalog' }, { label: 'Prizes' }]}
        description="Units are the stock truth; reservations and wins move units between buckets in one transaction (§6.2)."
      />
      <DataTable
        caption="Prizes"
        columns={columns}
        rows={q.data ?? []}
        rowKey={(r) => r.prizeId}
        loading={q.isPending}
        emptyState={
          <EmptyState
            title="No prizes yet"
            body="Prizes are created in the lottery wizard (Prizes step) from catalogue products."
            action={<Button href="/lotteries/new">Create lottery</Button>}
          />
        }
      />
    </>
  );
}

export function PrizeDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { toast } = useToast();
  const q = useQuery({ queryKey: ['prize', id], queryFn: () => catalogApi.prizes.get(id!) });
  const [action, setAction] = useState<'ADD' | 'RESERVE_MAIN' | 'RELEASE_MAIN'>('ADD');
  const [units, setUnits] = useState(1);
  const [variantId, setVariantId] = useState('');
  const adjust = useMutation({
    mutationFn: (body: StockAdjustRequest) => catalogApi.prizes.adjustStock(id!, body),
    onSuccess: (p) => {
      qc.setQueryData(['prize', id], p);
      void qc.invalidateQueries({ queryKey: ['prizes'] });
      toast({ title: 'Stock updated', tone: 'success' });
    },
  });
  if (q.isError)
    return (
      <ErrorState
        title="Prize not found"
        body="It may belong to another tenant."
        reference={isApiError(q.error) ? q.error.supportReference : undefined}
        action={<Button href="/catalog/prizes">Back</Button>}
      />
    );
  const p: PrizeDetailDto | undefined = q.data;
  return (
    <>
      <PageHeader
        title={p?.name.en ?? 'Prize'}
        breadcrumb={[
          { label: 'Sponsors & prizes', to: '/catalog' },
          { label: 'Prizes', to: '/catalog/prizes' },
          { label: p?.name.en ?? '…' },
        ]}
        meta={
          p
            ? `${p.tier} · ${p.selectionMode === 'VALUE_CAP' ? 'Mode B' : 'Mode A'} · ${money(p.value)}`
            : undefined
        }
      />
      <div className="wdc-stats" data-testid="stock-buckets">
        <StatCard label="Catalogue stock" value={p?.stock.catalogStock ?? null} loading={q.isPending} />
        <StatCard label="Available" value={p?.stock.available ?? null} loading={q.isPending} />
        <StatCard label="Main-draw reserved" value={p?.stock.mainReserved ?? null} loading={q.isPending} />
        <StatCard
          label="Sampling reserved"
          value={p?.stock.samplingReserved ?? null}
          loading={q.isPending}
          signal={p?.reservationLocked ? { tone: 'warning', text: 'Locked: cannot be reduced' } : undefined}
        />
        <StatCard
          label="Won"
          value={p?.stock.won ?? null}
          loading={q.isPending}
          hint={p ? `${p.stock.held} held` : undefined}
        />
        <StatCard label="Paid / delivered" value={p?.stock.fulfilled ?? null} loading={q.isPending} />
      </div>
      {p?.assetIssues.length ? (
        <div className="wdc-callout" role="status">
          <strong>Asset issues:</strong> {p.assetIssues.map((i) => `${i.ref}: ${i.issue}`).join(' · ')}
        </div>
      ) : null}
      {p?.variants.length ? (
        <table className="wdc-table" data-testid="variant-stock">
          <caption>Stock per SKU</caption>
          <thead>
            <tr>
              <th>SKU</th>
              <th>Options</th>
              <th>Value</th>
              <th>Available</th>
              <th>Reserved</th>
              <th>Won</th>
              <th>Delivered</th>
            </tr>
          </thead>
          <tbody>
            {p.variants.map((v) => (
              <tr key={v.variantId}>
                <td>
                  <code>{v.sku}</code>
                </td>
                <td>
                  {Object.entries(v.options)
                    .map(([k, x]) => `${k}: ${x}`)
                    .join(', ')}
                </td>
                <td>{money(v.value)}</td>
                <td>{v.stock.available}</td>
                <td>{v.stock.mainReserved + v.stock.samplingReserved}</td>
                <td>{v.stock.won}</td>
                <td>{v.stock.fulfilled}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      <Can permission="prizes.manage">
        <h2>Adjust stock</h2>
        <div className="wdc-form-grid">
          <Field label="Action">
            <Select
              value={action}
              onChange={(e) => setAction(e.target.value as typeof action)}
              options={[
                { value: 'ADD', label: 'Add units' },
                { value: 'RESERVE_MAIN', label: 'Reserve for main draw' },
                { value: 'RELEASE_MAIN', label: 'Release main-draw reservation' },
              ]}
            />
          </Field>
          <Field label="Units">
            <Input
              type="number"
              min={1}
              value={units}
              onChange={(e) => setUnits(Math.max(1, Number(e.target.value) || 1))}
            />
          </Field>
          {p?.variants.length ? (
            <Field label="SKU">
              <Select
                value={variantId}
                onChange={(e) => setVariantId(e.target.value)}
                options={[
                  { value: '', label: 'Any' },
                  ...p.variants.map((v) => ({ value: v.variantId, label: v.sku })),
                ]}
              />
            </Field>
          ) : null}
        </div>
        <Button
          variant="secondary"
          loading={adjust.isPending}
          onClick={() =>
            adjust.mutate({ action, units, ...(variantId ? { variantId } : {}) } as StockAdjustRequest)
          }
        >
          Apply
        </Button>
        {adjust.error ? (
          <p role="alert" className="text-error">
            {adjust.error.message}
          </p>
        ) : null}
      </Can>
    </>
  );
}
